// Admin user page → "Behavior (PostHog)": how this person first arrived, where
// they are, which funnel steps they hit, their screen recordings and recent
// activity. Data: GET /admin/analytics/users/:id/posthog.
import { useEffect, useState } from "react";
import { axiosInstance } from "../../config";

const MILESTONE_LABEL = {
  get_clips_click: "Clicked Get Clips",
  signed_up: "Signed up (email)",
  stream_connect_clicked: "Clicked connect stream",
  onboarding_completed: "Finished onboarding",
  paywall_viewed: "Saw the paywall",
  paywall_reopened: "Paywall re-opened (tried to use app)",
  checkout_started: "Started checkout",
};

const EVENT_LABEL = {
  $pageview: "Viewed",
  $pageleave: "Left",
  $autocapture: "Clicked",
  $rageclick: "Rage-clicked",
  $identify: "Identified",
  ...MILESTONE_LABEL,
};

function when(d) {
  if (!d) return "—";
  const t = new Date(d);
  const s = Math.floor((Date.now() - t.getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24); if (days < 30) return `${days}d ago`;
  return t.toLocaleDateString();
}

function shortUrl(u) {
  if (!u) return "";
  try { const x = new URL(u); return `${x.host.replace(/^www\./, "")}${x.pathname}`; } catch { return u; }
}

function duration(sec) {
  if (!sec) return "0s";
  const m = Math.floor(sec / 60), s = sec % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
}

function Row({ label, value }) {
  return (
    <div className="aph-row">
      <span className="aph-label">{label}</span>
      <span className="aph-value">{value || <span className="muted">—</span>}</span>
    </div>
  );
}

export default function UserPostHog({ userId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get(`/admin/analytics/users/${userId}/posthog`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setData({ configured: true, error: e.message }); });
    return () => { cancelled = true; };
  }, [userId]);

  let body;
  if (!data) body = <div className="aud-empty-sm">Loading PostHog…</div>;
  else if (data.configured === false) {
    body = (
      <div className="aud-empty-sm">
        Not connected. Add <b>POSTHOG_PERSONAL_API_KEY</b> on the API service in Render.
      </div>
    );
  } else if (data.error) body = <div className="aud-empty-sm">Couldn't load PostHog: {data.error}</div>;
  else if (!data.found) {
    body = (
      <div className="aud-empty-sm">
        No PostHog activity for this user yet (signed up before tracking, or hasn't visited since).
      </div>
    );
  } else {
    const p = data.profile.ok ? data.profile.data : null;
    const s = data.summary.ok ? data.summary.data : null;
    const loc = p ? [p.location.city, p.location.region, p.location.country].filter(Boolean).join(", ") : "";
    const device = p ? [p.device.type, p.device.os, p.device.browser].filter(Boolean).join(" · ") : "";
    const ft = p?.firstTouch || {};
    body = (
      <>
        <div className="aph-grid">
          <div className="aph-card">
            <div className="aph-card-title">First touch</div>
            <Row label="Source" value={ft.source && `${ft.source}${ft.medium ? ` / ${ft.medium}` : ""}`} />
            <Row label="Campaign" value={ft.campaign} />
            <Row label="Ad" value={ft.content} />
            <Row label="Referrer" value={ft.referrer} />
            <Row label="Landed on" value={ft.landingPage && shortUrl(ft.landingPage)} />
          </div>
          <div className="aph-card">
            <div className="aph-card-title">Who & where</div>
            <Row label="Location" value={loc} />
            <Row label="Device" value={device} />
            <Row label="First seen" value={s && when(s.firstSeen)} />
            <Row label="Last seen" value={s && when(s.lastSeen)} />
            <Row label="Sessions" value={s && `${s.sessions} · ${s.pageviews} pageviews`} />
          </div>
        </div>

        <div className="aph-card">
          <div className="aph-card-title">Funnel</div>
          {data.milestones.ok ? (
            <div className="aph-steps">
              {data.milestones.data.map((m) => (
                <div key={m.event} className={`aph-step${m.at ? " done" : ""}`}>
                  <span className="aph-step-dot">{m.at ? "✓" : ""}</span>
                  <span className="aph-step-label">{MILESTONE_LABEL[m.event] || m.event}</span>
                  <span className="aph-step-when muted">{m.at ? `${when(m.at)}${m.count > 1 ? ` · ×${m.count}` : ""}` : "—"}</span>
                </div>
              ))}
            </div>
          ) : <div className="aud-empty-sm">{data.milestones.error}</div>}
        </div>

        <div className="aph-card">
          <div className="aph-card-title">🎥 Screen recordings</div>
          {!data.recordings.ok ? (
            <div className="aud-empty-sm">Recordings unavailable: {data.recordings.error}</div>
          ) : data.recordings.data.length === 0 ? (
            <div className="aud-empty-sm">
              No recordings yet. Turn on PostHog → Settings → Session replay → "Record user sessions".
            </div>
          ) : (
            <div className="aph-recs">
              {data.recordings.data.map((r) => (
                <a key={r.id} className="aph-rec" href={r.url} target="_blank" rel="noopener noreferrer">
                  <span className="aph-rec-play">▶</span>
                  <span className="aph-rec-main">
                    <span className="aph-rec-when">{when(r.started)} · {duration(r.activeSeconds)} active · {r.clicks} clicks</span>
                    <span className="aph-rec-url muted">{shortUrl(r.firstUrl)}</span>
                  </span>
                  <span className="aph-rec-cta">Watch →</span>
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="aph-card">
          <div className="aph-card-title">Recent activity</div>
          {!data.recent.ok ? (
            <div className="aud-empty-sm">{data.recent.error}</div>
          ) : (
            <div className="aph-timeline">
              {data.recent.data.map((e, i) => (
                <div key={i} className="aph-event">
                  <span className="aph-event-what">
                    {EVENT_LABEL[e.event] || e.event}
                    {e.text ? <span className="muted"> “{e.text.slice(0, 40)}”</span> : null}
                  </span>
                  <span className="aph-event-where muted">{shortUrl(e.url)}</span>
                  <span className="aph-event-when muted">{when(e.at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <div className="aud-section">
      <h2>
        Behavior <span className="muted" style={{ fontSize: 13, fontWeight: 500 }}>PostHog</span>
        {data?.personUrl && (
          <a className="aph-open" href={data.personUrl} target="_blank" rel="noopener noreferrer">Open in PostHog →</a>
        )}
      </h2>
      {body}
    </div>
  );
}
